import { Button, DatePicker, Form, Modal, Select, Space } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';

import { ShiftListItemDto, ShiftTime } from '@clh/api-client';
import { unwrapError, useApiClient } from '@clh/ui';

import { useToast } from '../../hooks/use-toast';

interface ScheduleChangeModalProps {
    shift: ShiftListItemDto;
    open: boolean;
    onClose: () => void;
    onSuccess?: (values: {
        startTime?: Date;
        endTime?: Date;
        shiftTime: ShiftTime;
    }) => Promise<void>;
}

export default function ScheduleChangeModal({
    shift,
    open,
    onClose,
    onSuccess,
}: ScheduleChangeModalProps) {
    const [form] = Form.useForm();
    const api = useApiClient();
    const [loading, setLoading] = useState(false);
    const { notification } = useToast();

    const handleOk = () => {
        form.submit();
    };

    const handleFinish = async (values: {
        startTime?: Dayjs;
        endTime?: Dayjs;
        shiftTime: ShiftTime;
    }) => {
        setLoading(true);
        try {
            if (values.shiftTime === ShiftTime.Custom) {
                if (!values.startTime || !values.endTime) {
                    notification.error({
                        message:
                            'Both start and end times are required for custom shifts',
                        duration: 3,
                    });
                    setLoading(false);
                    return;
                }

                const startTime = values.startTime.format('HH:mm');
                const endTime = values.endTime.format('HH:mm');

                await api?.shiftControllerUpdateShift({
                    shiftId: shift.id,
                    shiftUpdateDto: {
                        startDate: values.startTime.toDate(),
                        customStartTime: startTime,
                        customEndTime: endTime,
                        shiftTime: ShiftTime.Custom,
                    },
                });
            } else {
                await api?.shiftControllerUpdateShift({
                    shiftId: shift.id,
                    shiftUpdateDto: {
                        startDate: values.startTime?.toDate(),
                        shiftTime: values.shiftTime,
                    },
                });
            }

            notification.success({
                message: 'Shift schedule updated successfully',
                duration: 3,
            });
            onClose();
            await onSuccess?.({
                ...values,
                startTime: values.startTime?.toDate(),
                endTime: values.endTime?.toDate(),
            });
        } catch (err) {
            notification.error({
                message: unwrapError(err),
                duration: 3,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title="Change Shift Schedule"
            open={open}
            onCancel={onClose}
            footer={[
                <Button key="back" onClick={onClose}>
                    Cancel
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={loading}
                    onClick={handleOk}
                >
                    Update Schedule
                </Button>,
            ]}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={(values) => void handleFinish(values)}
                initialValues={{
                    startTime: dayjs(shift.startTime),
                    endTime: dayjs(shift.endTime),
                    shiftTime: shift.shiftTime || 'CUSTOM',
                }}
            >
                <Form.Item
                    name="shiftTime"
                    label="Shift Time"
                    rules={[
                        {
                            required: true,
                            message: 'Please select a shift time',
                        },
                    ]}
                >
                    <Select
                        virtual={false}
                        options={Object.values(ShiftTime).map((time) => ({
                            value: time,
                            label: time,
                        }))}
                    />
                </Form.Item>

                <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) =>
                        prevValues.shiftTime !== currentValues.shiftTime
                    }
                >
                    {({ getFieldValue }) => {
                        const shiftTime = getFieldValue('shiftTime');

                        if (shiftTime === 'CUSTOM') {
                            return (
                                <Space
                                    direction="vertical"
                                    style={{ width: '100%' }}
                                >
                                    <Form.Item
                                        name="startTime"
                                        label="Start Time"
                                        rules={[
                                            {
                                                required: true,
                                                message:
                                                    'Please select a start time',
                                            },
                                        ]}
                                    >
                                        <DatePicker
                                            showTime={{ format: 'HH:mm' }}
                                            format="YYYY-MM-DD HH:mm"
                                            style={{ width: '100%' }}
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        name="endTime"
                                        label="End Time"
                                        rules={[
                                            {
                                                required: true,
                                                message:
                                                    'Please select an end time',
                                            },
                                        ]}
                                    >
                                        <DatePicker
                                            showTime={{ format: 'HH:mm' }}
                                            format="YYYY-MM-DD HH:mm"
                                            style={{ width: '100%' }}
                                        />
                                    </Form.Item>
                                </Space>
                            );
                        }

                        return (
                            <Form.Item
                                name="startTime"
                                label="Shift Date"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Please select a date',
                                    },
                                ]}
                            >
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format="YYYY-MM-DD"
                                />
                            </Form.Item>
                        );
                    }}
                </Form.Item>
            </Form>
        </Modal>
    );
}
